import type * as THREE from 'three';
import { SENDA, SENDA_SWITCH, sendaChapters, sendaReady, type HeroClass, type SendaAct, type SendaDef } from '@somnhold/content';
import { chapterLevel, sendaAct, sendaBlocked, sendaDef, sendaOf, sendaOpensAt, heroName, type CampaignState, type HeroState, type PlayerState, type SendaState } from '@somnhold/sim';
import { castKey, heroKey, renderThumb, sendaCast, type StatueLook } from '@somnhold/render';
import { heroLook } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { fmtDuration } from '../ui/format.ts';
import { ICONS } from '../ui/icons.ts';
import { heroLegacy, legacyTitle, nameChip, SIGIL } from '../ui/name-chip.ts';

/**
 * The path's card (docs/SENDAS.md): a hero's own story, one chapter after another. The chapter and the act it is at,
 * its three tries (and the ones friends gave, with their names), why it is shut and when it opens, the way in, the
 * pieces of the look it earned and the chapters to play again. A friend's card offers to give a try.
 */

export interface SendaActions {
  /** Goes on with the path where it is (a scene, the campfire, the decision, a fight). */
  play: () => void;
  /** Plays a finished chapter again from its start (-1: stops playing it again). */
  again: (ch: number) => void;
  /** Gives the friend's hero a try. */
  gift: (to: string, cls: HeroClass) => void;
  me: string;
  now: () => number;
  renderer: THREE.WebGLRenderer | null;
}

const k = (def: SendaDef): string => `senda.${def.id}`;

/** The players' name (the hero's own, or the player's). */
const nameOfPid = (s: CampaignState, pid: string): string => {
  const p = s.players.find((x) => x.id === pid);
  return p ? heroName(p) : '—';
};

/** A face of the path: one of its people (the cast's miniature) or the hero, head and shoulders. */
const faces = new Map<string, string>();
export const sendaFace = (renderer: THREE.WebGLRenderer | null, key: string): string | null => {
  if (!renderer) return null;
  let url = faces.get(key);
  if (!url) {
    try {
      url = renderThumb(renderer, key, 160, 0, { bust: true, face: true });
    } catch {
      return null;
    }
    faces.set(key, url);
  }
  return url;
};

/** The library key of `who`'s miniature in a hero's path (the hero itself: its own). */
export const whoKey = (hero: HeroState, cls: HeroClass, who: string): string => {
  if (who === 'hero') return heroKey(hero.heroClass, hero.level, false, heroLook(hero));
  // (Someone drawn lying down has a standing miniature for the face: Tessa on her stretcher.)
  const c = sendaCast(cls, who);
  return castKey(c?.portrait ? { ...c, model: c.portrait } : c, cls, (x) => x);
};

/** The tries as marks: the hero's three, then each friend's (with the name), spent ones hollow. */
export const triesMarks = (s: CampaignState, st: SendaState, open: boolean): HTMLElement => {
  const gifts = (st.gifts ?? []).filter((g) => g.ch === st.ch);
  const used = gifts.filter((g) => g.used);
  // A round open again starts with every try; out of the hero's own, the last friend's used one is in play.
  const left = open && st.closed ? SENDA.tries : st.left;
  const own = used.length ? 0 : left;
  const marks = h('span.sd-marks', { 'aria-label': t('senda.ui.triesLeft', { n: left, of: SENDA.tries }) });
  for (let i = 0; i < SENDA.tries; i++) marks.append(h(`i.sd-mark${i < own ? '.on' : ''}`));
  const giftRows = gifts.map((g, i) => {
    const playing = g.used && i === gifts.indexOf(used[used.length - 1] as (typeof gifts)[number]) && left > 0;
    const state = playing ? t('senda.ui.triesLeft', { n: 1, of: 1 }) : g.used ? t('senda.ui.giftUsed') : t('senda.ui.giftWaits');
    marks.append(h(`i.sd-mark.gift${playing || !g.used ? '.on' : ''}`));
    return h('li', {}, h('i.sd-dot'), h('span', {}, t('senda.ui.giftBy', { name: nameOfPid(s, g.pid) })), h('small', {}, state));
  });
  return h(
    'div.sd-tries',
    { 'data-test': 'senda-tries' },
    h('div.sd-tries-row', {}, h('span.label', {}, t('senda.ui.tries')), marks, h('span.small', {}, t('senda.ui.triesLeft', { n: own, of: SENDA.tries }))),
    giftRows.length ? h('ul.sd-gifts', {}, ...giftRows) : '',
  );
};

/** What an act is, by kind (the chapter's test said as such). */
const kindOf = (a: SendaAct): string => (a.kind === 'fight' && a.trial ? t('senda.ui.kind.trial') : t(`senda.ui.kind.${a.kind}`));
const ACT_ICON: Record<SendaAct['kind'], string> = { scene: ICONS.scroll, fire: ICONS.ember, choice: ICONS.quill, fight: ICONS.sword };

/** The chapter's acts as a row of stones: done, the one it is at, the ones to come. */
const actRow = (def: SendaDef, ch: number, act: number): HTMLElement =>
  h(
    'ol.sd-acts',
    {},
    ...(def.chapters[ch]?.acts ?? []).map((a, i) => h(`li${i < act ? '.done' : i === act ? '.now' : ''}`, { title: kindOf(a) }, h('span', { html: ACT_ICON[a.kind] }))),
  );

/** The act the hero is at, and the way in (on a friend's card, only what it is). */
const nowBlock = (s: CampaignState, p: PlayerState, def: SendaDef, a: SendaActions, mine: boolean): HTMLElement => {
  const hero = p.hero;
  const now = a.now();
  const st = sendaOf(hero) as SendaState;
  const again = !!st.again;
  const at = sendaAct(hero, now, again);
  const ch = at ? def.chapters[at.ch] : undefined;
  const box = h('div.sd-now', { 'data-test': 'senda-now' });
  if (!at || !ch) return box;
  const act = at.def;
  const base = `${k(def)}.${ch.id}.${act.id}`;
  box.append(h('div.sd-now-head', {}, h('span.sd-kind', {}, h('span', { html: ACT_ICON[act.kind] }), kindOf(act)), h('span.small', {}, t('senda.ui.act', { n: at.act + 1, of: ch.acts.length }))));
  if (act.kind === 'fight') {
    box.append(
      h('b.sd-act-title', {}, t(`${base}.title`)),
      h('p.sd-goal', {}, t(`${base}.goal`)),
      h('dl.sd-rules', {}, h('dt', {}, t('senda.ui.win')), h('dd', {}, t(`${base}.win`)), h('dt.lose', {}, t('senda.ui.lose')), h('dd', {}, t(`${base}.lose`))),
    );
  } else {
    const who = act.kind === 'fire' ? act.with[0] : act.lines.find((l) => l.who !== 'narrator' && l.who !== 'hero')?.who;
    box.append(h('p.sd-goal', {}, who ? t('senda.ui.waits', { who: t(`${k(def)}.npc.${who}`) }) : t('senda.ui.goes')));
  }
  if (mine) box.append(h('div.sd-go', {}, h('button.btn', { 'data-test': 'senda-play', onclick: () => a.play() }, t(`senda.ui.go.${act.kind}`))));
  return box;
};

/** Why the path can't go on now, and when it can. */
const blockBlock = (s: CampaignState, p: PlayerState, def: SendaDef, a: SendaActions, mine: boolean, renderer: THREE.WebGLRenderer | null): HTMLElement | null => {
  const hero = p.hero;
  const now = a.now();
  const st = sendaOf(hero) as SendaState;
  const b = sendaBlocked(hero, now);
  if (!b || st.again) return null;
  const at = sendaOpensAt(hero, now);
  const when = at ? fmtDuration(at - now) : '';
  if (b === 'over') return h('div.sd-block.over', { 'data-test': 'senda-block' }, h('span', { html: ICONS.check }), h('p', {}, t('senda.ui.over')));
  // Its next chapter not ready yet (SENDA_READY): no date, it opens with a later deploy.
  if (b === 'soon') return h('div.sd-block.soon', { 'data-test': 'senda-block' }, h('span', { html: ICONS.hourglass }), h('p', {}, t('senda.ui.blockSoon')));
  if (b === 'level') return h('div.sd-block', { 'data-test': 'senda-block' }, h('span', { html: ICONS.lock }), h('p', {}, t(mine ? 'senda.ui.blockLevel' : 'senda.ui.blockLevelOther', { n: chapterLevel(def, st.ch), l: hero.level })));
  if (b === 'wait') return h('div.sd-block', { 'data-test': 'senda-block' }, h('span', { html: ICONS.hourglass }), h('p', {}, t('senda.ui.blockWait', { n: st.ch + 1, t: when })));
  if (b === 'closed') {
    const ch = def.chapters[st.ch];
    const face = ch ? sendaFace(renderer, whoKey(hero, def.cls, ch.closedBy)) : null;
    return h(
      'div.sd-block.closed',
      { 'data-test': 'senda-block' },
      ch
        ? h(
            'figure.sd-quote',
            {},
            face ? h('img.sd-quote-face', { src: face, alt: '' }) : h('span.sd-quote-face'),
            h('blockquote', {}, h('p', {}, `«${t(`${k(def)}.${ch.id}.closed`)}»`), h('cite', {}, t(`${k(def)}.npc.${ch.closedBy}`))),
          )
        : '',
      h('p.small', {}, t('senda.ui.blockClosed', { t: when })),
    );
  }
  return h('div.sd-block', {}, h('p', {}, t('senda.ui.none')));
};

/** A friend's card: give a try (one per friend and chapter, two at most), or why not. */
const giftBlock = (s: CampaignState, p: PlayerState, def: SendaDef, a: SendaActions): HTMLElement | null => {
  const hero = p.hero;
  const now = a.now();
  const b = sendaBlocked(hero, now);
  if (b && b !== 'closed') return null;
  const st = sendaOf(hero) as SendaState;
  const gifts = (st.gifts ?? []).filter((g) => g.ch === st.ch);
  const given = gifts.some((g) => g.pid === a.me);
  const full = gifts.length >= SENDA.gifts;
  return h(
    'div.sd-give',
    { 'data-test': 'senda-give' },
    given || full
      ? h('p.small', {}, t(given ? 'senda.ui.given' : 'senda.ui.giftsFull'))
      : h('button.btn', { 'data-test': 'senda-gift', onclick: () => a.gift(p.id, hero.heroClass) }, h('span', { html: ICONS.sparkle }), ' ', t('senda.ui.give')),
    given || full ? '' : h('p.small', {}, t('senda.ui.giftHint')),
  );
};

/** The look's pieces each chapter gave, and the ones still to earn. */
const piecesBlock = (def: SendaDef, st: SendaState): HTMLElement => {
  const got = st.pieces ?? [];
  return h(
    'div.sd-pieces',
    { 'data-test': 'senda-pieces' },
    h('span.label', {}, t('senda.ui.pieces')),
    h(
      'ul',
      {},
      ...def.chapters.map((c, i) =>
        c.piece && got.includes(c.piece)
          ? h('li.got', {}, h('span', { html: ICONS.sparkle }), h('span', {}, t(`${k(def)}.${c.id}.piece`)))
          : h('li', {}, h('span', { html: ICONS.lock }), h('span', {}, t('senda.ui.pieceSlot', { n: i + 1 }))),
      ),
    ),
  );
};

/** Finished chapters to play again (free, no prize, no tries); the one being played again, to go on or leave. */
const againBlock = (def: SendaDef, st: SendaState, a: SendaActions): HTMLElement | null => {
  const done = def.chapters.slice(0, Math.min(st.ch, def.chapters.length));
  if (!done.length) return null;
  const box = h('div.sd-again', { 'data-test': 'senda-again' }, h('span.label', {}, t('senda.ui.againTitle')));
  if (st.again) {
    box.append(
      h('p', {}, t('senda.ui.againNow', { n: st.again.ch + 1 })),
      h('div.sd-go', {}, h('button.btn', { 'data-test': 'senda-again-go', onclick: () => a.play() }, t('senda.ui.againGo')), h('button.btn.ghost', { 'data-test': 'senda-again-stop', onclick: () => a.again(-1) }, t('senda.ui.againStop'))),
    );
    return box;
  }
  box.append(
    h('div.sd-again-list', {}, ...done.map((c, i) => h('button.btn.ghost.small', { 'data-test': `senda-again-${i}`, onclick: () => a.again(i) }, t('senda.ui.chapter', { n: i + 1, of: sendaChapters(def), title: t(`${k(def)}.${c.id}.title`) })))),
    h('p.small', {}, t('senda.ui.againHint')),
  );
  return box;
};

/** The whole card: `pid`'s path (your own, or a friend's). */
export const sendaCard = (s: CampaignState, pid: string, a: SendaActions): HTMLElement => {
  const p = s.players.find((x) => x.id === pid);
  const mine = pid === a.me;
  if (!p) return h('div.senda-card', {}, h('p', {}, '—'));
  const def = sendaDef(p.hero);
  const st = sendaOf(p.hero);
  if (!def || !st) return h('div.senda-card', { 'data-test': 'senda-card' }, h('p', {}, t('senda.ui.none')));
  const now = a.now();
  const b = sendaBlocked(p.hero, now);
  const chIdx = Math.min(st.again?.ch ?? st.ch, def.chapters.length - 1);
  const ch = def.chapters[chIdx];
  const legacy = heroLegacy(p.hero);
  const over = st.ch >= def.chapters.length;
  const actIdx = st.again ? st.again.act : over ? (ch?.acts.length ?? 0) : st.act;
  return h(
    'div.senda-card',
    { 'data-test': 'senda-card' },
    h(
      'header.sd-head',
      {},
      h('span.sd-sigil', { html: SIGIL[def.cls] }),
      h(
        'div',
        {},
        h('span.sd-kicker', {}, t(`class.${def.cls}`)),
        h('h3', {}, t(`${k(def)}.title`)),
        ch ? h('span.small', {}, t('senda.ui.chapter', { n: chIdx + 1, of: sendaChapters(def), title: t(`${k(def)}.${ch.id}.title`) })) : '',
      ),
    ),
    h('p.sd-question', {}, t(`${k(def)}.question`)),
    legacy ? h('div.sd-legacy', {}, h('span.label', {}, t('senda.ui.legacy')), nameChip(heroName(p), legacy)) : '',
    ch && !over ? actRow(def, chIdx, actIdx) : '',
    !over || st.again ? triesMarks(s, st, b !== 'closed') : '',
    blockBlock(s, p, def, a, mine, a.renderer) ?? '',
    !b || st.again ? nowBlock(s, p, def, a, mine) : '',
    mine ? '' : (giftBlock(s, p, def, a) ?? ''),
    mine && !b && !st.again ? h('p.small.sd-alone', {}, t('senda.ui.alone')) : '',
    piecesBlock(def, st),
    mine ? (againBlock(def, st, a) ?? '') : '',
  );
};

/** Whether `p`'s hero has a path to show (its class has one written, and it reached its first chapter or began it). */
export const sendaShown = (p: PlayerState): boolean => {
  const def = sendaDef(p.hero);
  // (A class whose path is off in SENDA_READY shows none.)
  if (!def || !sendaReady(def)) return false;
  return !!p.hero.senda || p.hero.level >= chapterLevel(def, 0);
};

/**
 * The paths' statues (docs/SENDAS.md, «El legado»): everyone who walked one to its end, as they look now if they still
 * play that class here (else the class's plain miniature: it stays if the hero retires), on the plaque with its title.
 */
export const sendaStatues = (s: CampaignState): StatueLook | null => {
  if (!SENDA_SWITCH.on) return null;
  const recs = Object.entries(s.sendas ?? {}).flatMap(([id, rs]) => rs.map((r) => ({ id, r })));
  if (!recs.length) return null;
  const ids = [...new Set(recs.map((x) => x.id))];
  const figures = recs.slice(0, 4).map(({ r }) => {
    const p = s.players.find((x) => x.id === r.pid);
    const hero = p && p.hero.heroClass === r.cls ? p.hero : undefined;
    const title = legacyTitle(r.cls);
    return { key: hero ? heroKey(r.cls, hero.level, false, heroLook(hero)) : heroKey(r.cls, 10), name: title ? `${r.name}, ${title}` : r.name };
  });
  return { title: ids.length === 1 ? t(`senda.${ids[0]}.title`) : t('senda.ui.statues'), figures };
};
